/**
 * TimetableFlightEditModal — Timetable Grid 行操作弹窗编辑器。
 *
 * 提交时构建 UPDATE_FLIGHT_FIELDS EditCommand 交给 editPipeline.executeCommand，
 * 由 Pipeline 执行 validate → recalculate → apply 到 localState。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → components/ScheduleDraftFlightForm.jsx (表单体),
 *             hooks/useScheduleDraftEditPipeline.js (Pipeline),
 *             components/editor/timetable/ScheduleDraftTimetableSection.jsx (装配层)
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Modal, message } from "antd";
import { UPDATE_FLIGHT_FIELDS } from "../../../lib/editCommand";
import useFlightEditRouteReferenceData from "../../../hooks/useFlightEditRouteReferenceData";
import ScheduleDraftFlightForm from "../../ScheduleDraftFlightForm";
import { buildFlightFormValues } from "./timetableRowModel";

const TimetableFlightEditModal = ({ editingFlight, draftId, onClose, executeCommand, canUpdateFlight }) => {
    const [editForm] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const routeReferenceDataRef = useRef({});
    const loadRouteReferenceDataForEdit = useFlightEditRouteReferenceData();
    const isDeletedFlight = String(editingFlight?.change_status || "").toUpperCase() === "DELETED";

    const handleReferenceDataChange = useCallback((referenceData) => {
        routeReferenceDataRef.current = referenceData || {};
    }, []);

    useEffect(() => {
        if (!editingFlight) {
            routeReferenceDataRef.current = {};
            editForm.resetFields();
            return;
        }

        editForm.setFieldsValue(buildFlightFormValues(editingFlight));
    }, [editForm, editingFlight]);

    const handleSubmit = async () => {
        if (!editingFlight || !executeCommand || isDeletedFlight) {
            return;
        }

        try {
            const values = await editForm.validateFields();
            setSubmitting(true);
            const referenceData = await loadRouteReferenceDataForEdit({
                values,
                currentFlight: editingFlight,
                referenceData: routeReferenceDataRef.current,
            });

            const result = await executeCommand({
                type: UPDATE_FLIGHT_FIELDS,
                draftId,
                flightId: editingFlight.id,
                payload: values,
                meta: {
                    source: "modal",
                    referenceData,
                },
            });

            // 仅在 Pipeline 成功时关闭弹窗
            if (result.status === "success") {
                onClose();
            }
        } catch (error) {
            // Antd form validation errors have errorFields
            if (error?.errorFields) {
                return;
            }
            if (error?.code === "ROUTE_REFERENCE_DATA_LOAD_FAILED") {
                message.error(error.message ? `航路数据加载失败，修改未应用：${error.message}` : "航路数据加载失败，修改未应用");
                return;
            }
            throw error;
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={`编辑航班 ${editingFlight?.flight_number || ""}`.trim()}
            open={Boolean(editingFlight)}
            onCancel={onClose}
            onOk={handleSubmit}
            okText="应用修改"
            cancelText="取消"
            confirmLoading={submitting}
            okButtonProps={{ disabled: !canUpdateFlight || isDeletedFlight }}
            destroyOnClose
        >
            <Form form={editForm} layout="vertical" disabled={!canUpdateFlight || isDeletedFlight}>
                <ScheduleDraftFlightForm editable={canUpdateFlight && !isDeletedFlight} onReferenceDataChange={handleReferenceDataChange} />
            </Form>
        </Modal>
    );
};

export default TimetableFlightEditModal;
